import { RIGHT_SIDEBAR_DEFAULT } from "@/features/project/types";
import {
  DEFAULT_SPELLCHECK_ENABLED,
  DEFAULT_SPELLCHECK_LANGUAGE,
  isSupportedSpellcheckLanguage,
  type SupportedSpellcheckLanguage,
} from "@/features/rich-text/spellcheck/types";

export const PROFILE_DEFAULT_SIDEBAR_WIDTH = 320;
export const PROFILE_DEFAULT_RIGHT_PANEL_WIDTH = RIGHT_SIDEBAR_DEFAULT;

export type ThemePreference = "system" | "light" | "dark";
/** The stored preference is one of the languages the proofreader actually ships. */
export type SpellcheckLanguagePreference = SupportedSpellcheckLanguage;
export type AssistantAddressingPreference = "first_name" | "neutral";
export type AssistantVerbosityPreference = "concise" | "balanced" | "detailed";

export const PROFILE_DEFAULT_SPELLCHECK_ENABLED = DEFAULT_SPELLCHECK_ENABLED;
export const PROFILE_DEFAULT_SPELLCHECK_LANGUAGE: SpellcheckLanguagePreference =
  DEFAULT_SPELLCHECK_LANGUAGE;
export const PROFILE_DEFAULT_ASSISTANT_ADDRESSING: AssistantAddressingPreference = "neutral";
export const PROFILE_DEFAULT_ASSISTANT_VERBOSITY: AssistantVerbosityPreference = "balanced";

interface ProfilePreferencesSnapshot {
  theme: ThemePreference;
  ui: {
    defaultSidebarWidth: number;
    defaultRightPanelWidth: number;
    autoCollapseSidebar: boolean;
  };
  spellcheck: {
    enabled: boolean;
    language: SpellcheckLanguagePreference;
  };
  assistant: {
    addressing: AssistantAddressingPreference;
    verbosity: AssistantVerbosityPreference;
  };
}

interface StoredProfilePreferences {
  theme?: ThemePreference;
  ui?: {
    defaultSidebarWidth?: number;
    defaultRightPanelWidth?: number;
    autoCollapseSidebar?: boolean;
  };
  spellcheck?: {
    enabled?: boolean;
    language?: string;
  };
  assistant?: {
    addressing?: AssistantAddressingPreference;
    verbosity?: AssistantVerbosityPreference;
  };
}

export function buildProfilePreferencesPayload(
  theme: ThemePreference,
  defaultSidebarWidth: number,
  defaultRightPanelWidth: number,
  autoCollapseSidebar: boolean,
  spellcheckEnabled = PROFILE_DEFAULT_SPELLCHECK_ENABLED,
  spellcheckLanguage: SpellcheckLanguagePreference = PROFILE_DEFAULT_SPELLCHECK_LANGUAGE,
  assistantAddressing: AssistantAddressingPreference = PROFILE_DEFAULT_ASSISTANT_ADDRESSING,
  assistantVerbosity: AssistantVerbosityPreference = PROFILE_DEFAULT_ASSISTANT_VERBOSITY
): ProfilePreferencesSnapshot {
  return {
    theme,
    ui: {
      defaultSidebarWidth,
      defaultRightPanelWidth,
      autoCollapseSidebar,
    },
    spellcheck: {
      enabled: spellcheckEnabled,
      language: spellcheckLanguage,
    },
    assistant: {
      addressing: assistantAddressing,
      verbosity: assistantVerbosity,
    },
  };
}

export function resolveSpellcheckLanguagePreference(
  value: string | null | undefined
): SpellcheckLanguagePreference {
  return isSupportedSpellcheckLanguage(value) ? value : PROFILE_DEFAULT_SPELLCHECK_LANGUAGE;
}

export function hydrateProfilePreferencesState(
  preferences: StoredProfilePreferences | null | undefined,
  activeTheme: ThemePreference,
  currentSidebarWidth: number,
  currentRightPanelWidth: number
) {
  const savedDefaultSidebarWidth =
    preferences?.ui?.defaultSidebarWidth ?? PROFILE_DEFAULT_SIDEBAR_WIDTH;
  const savedDefaultRightPanelWidth =
    preferences?.ui?.defaultRightPanelWidth ?? PROFILE_DEFAULT_RIGHT_PANEL_WIDTH;
  const autoCollapseSidebar = Boolean(preferences?.ui?.autoCollapseSidebar);
  const spellcheckEnabled = preferences?.spellcheck?.enabled ?? PROFILE_DEFAULT_SPELLCHECK_ENABLED;
  const spellcheckLanguage = resolveSpellcheckLanguagePreference(preferences?.spellcheck?.language);
  const assistantAddressing =
    preferences?.assistant?.addressing === "first_name" ? "first_name" : "neutral";
  const assistantVerbosity =
    preferences?.assistant?.verbosity === "concise" ||
    preferences?.assistant?.verbosity === "detailed"
      ? preferences.assistant.verbosity
      : "balanced";

  return {
    theme: activeTheme,
    defaultSidebarWidthDraft: String(currentSidebarWidth),
    defaultRightPanelWidthDraft: String(currentRightPanelWidth),
    savedDefaultSidebarWidth,
    savedDefaultRightPanelWidth,
    autoCollapseSidebar,
    spellcheckEnabled,
    spellcheckLanguage,
    assistantAddressing,
    assistantVerbosity,
  };
}

export function serializeProfilePreferences(
  theme: ThemePreference,
  defaultSidebarWidth: number,
  defaultRightPanelWidth: number,
  autoCollapseSidebar: boolean,
  spellcheckEnabled = PROFILE_DEFAULT_SPELLCHECK_ENABLED,
  spellcheckLanguage: SpellcheckLanguagePreference = PROFILE_DEFAULT_SPELLCHECK_LANGUAGE,
  assistantAddressing: AssistantAddressingPreference = PROFILE_DEFAULT_ASSISTANT_ADDRESSING,
  assistantVerbosity: AssistantVerbosityPreference = PROFILE_DEFAULT_ASSISTANT_VERBOSITY
) {
  return JSON.stringify(
    buildProfilePreferencesPayload(
      theme,
      defaultSidebarWidth,
      defaultRightPanelWidth,
      autoCollapseSidebar,
      spellcheckEnabled,
      spellcheckLanguage,
      assistantAddressing,
      assistantVerbosity
    )
  );
}
